import React from 'react';
import { Area, AreaChart, ResponsiveContainer, YAxis } from 'recharts';
import { Card, CardContent } from '../ui/card';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Button } from '../ui/button';
import { MoreHorizontal, TrendingUp, ArrowRight, TrendingDown } from 'lucide-react';
import { cn } from '../../lib/utils';

const CHANGE_CONFIG = {
  positive: { color: 'text-green-600', stroke: 'hsl(160, 45%, 35%)', Icon: TrendingUp },
  neutral:  { color: 'text-amber-500',  stroke: 'hsl(38, 55%, 45%)',  Icon: ArrowRight },
  negative: { color: 'text-red-500',    stroke: 'hsl(4, 65%, 45%)',   Icon: TrendingDown },
};

export function AreaTrendCard({ title, value, change, changeType = 'positive', xaxis = 'month', chartData, index = 0, className }) {
  const cfg = CHANGE_CONFIG[changeType] || CHANGE_CONFIG.positive;
  const gradientId = `areaTrend-${index}`;

  return (
    <Card className={cn('border-[#E7E0D6] dark:border-white/[0.06] bg-white dark:bg-[#1E2025]', className)}>
      <CardContent className="p-0">
        <section className="flex flex-nowrap justify-between">
          <div className="flex flex-col justify-between gap-y-2 p-4">
            <div className="flex flex-col gap-y-3">
              <dt className="text-sm font-medium text-[#5B616E] dark:text-white/60">{title}</dt>
              <dd className="text-3xl font-semibold text-[#121316] dark:text-white/90">{value}</dd>
            </div>
            <div className={cn('mt-2 flex items-center gap-x-1 text-xs font-medium', cfg.color)}>
              <cfg.Icon className="h-4 w-4" />
              <span>{change}</span>
              <span className="text-[#5B616E]">
                {' '}vs {xaxis === 'day' ? 'yesterday' : `last ${xaxis}`}
              </span>
            </div>
          </div>
          <div className="mt-10 min-h-[96px] w-36 min-w-[140px] shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart accessibilityLayer data={chartData}>
                <defs>
                  <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
                    <stop offset="5%" stopColor={cfg.stroke} stopOpacity={0.2} />
                    <stop offset="60%" stopColor={cfg.stroke} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <YAxis domain={[Math.min(...(chartData || []).map(d => d.value)), 'auto']} hide />
                <Area dataKey="value" fill={`url(#${gradientId})`} stroke={cfg.stroke} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="absolute top-2 right-2 h-7 w-7 rounded-full">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[120px]">
              <DropdownMenuItem className="text-xs">View Details</DropdownMenuItem>
              <DropdownMenuItem className="text-xs">Export Data</DropdownMenuItem>
              <DropdownMenuItem className="text-xs">Set Alert</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </section>
      </CardContent>
    </Card>
  );
}

export function AreaTrendGrid({ items }) {
  return (
    <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {items.map((item, index) => (
        <AreaTrendCard key={index} index={index} {...item} />
      ))}
    </div>
  );
}
